import { Inject, Injectable, InjectionToken } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { RoiStore, RoiChangeStore } from '../../stores';
import { SyncReviewedRoiChangesUseCase } from '@analysis/use-cases';
import { catchError, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, EMPTY, from, Observable, of, timer } from 'rxjs';
import { SnackbarService } from '@shared/ui';

export type ReviewAnalysisPayload = {
  sampleId: string;
  sourceAnalystId: string;
  currentAnalystId: string;
};

export const SYNC_REVIEWED_ROI_CHANGES_USE_CASE = new InjectionToken<SyncReviewedRoiChangesUseCase>(
  'SYNC_REVIEWED_ROI_CHANGES_USE_CASE'
);

const SNACKBAR_DEBOUNCE_MS = 300;

@Injectable()
export class RoiAnalysisReviewEffects {
  private snackbarRef: MatSnackBarRef<unknown> | undefined;
  private readonly snackbarTrigger$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    @Inject(SYNC_REVIEWED_ROI_CHANGES_USE_CASE)
    private readonly reviewAnalysisUseCase: SyncReviewedRoiChangesUseCase,
    private readonly snackbarService: SnackbarService
  ) {
    this.wireSnackbar(this.snackbarTrigger$);
  }

  readonly reviewAnalysis = this.roiStore.effect((payload$: Observable<ReviewAnalysisPayload>) =>
    payload$.pipe(
      switchMap(({ sampleId, sourceAnalystId, currentAnalystId }) => {
        this.roiChangeStore.setSyncing(true);
        this.snackbarTrigger$.next(true);
        return from(this.reviewAnalysisUseCase.execute({ sampleId, currentAnalystId, sourceAnalystId })).pipe(
          tap(({ updatedRois }) => {
            this.roiStore.completeSyncWithUpdatedRois(updatedRois);
            // Deliberately records no own-save versions: this writes findings for the CURRENT
            // user while the view reads the SOURCE analyst's partition, so nothing on screen
            // could match them - and `selectOwnAnalysis()` clears them with the generation.
            this.roiChangeStore.clearAllChanges();
          }),
          tap(() => this.snackbarService.show('analysis.review_saved')),
          map(() => undefined),
          catchError((error) => {
            this.roiChangeStore.setError(error.message);
            return EMPTY;
          }),
          finalize(() => this.snackbarTrigger$.next(false))
        );
      })
    )
  );

  private readonly wireSnackbar = this.roiStore.effect((flag$: Observable<boolean>) =>
    flag$.pipe(
      distinctUntilChanged(),
      switchMap((active) => (active ? timer(SNACKBAR_DEBOUNCE_MS).pipe(map(() => true)) : of(false))),
      tap((active: boolean) => {
        this.snackbarRef?.dismiss();
        this.snackbarRef = undefined;
        if (!active) return;
        this.snackbarRef = this.snackbarService.show('analysis.saving_review', {
          duration: 0,
          dismissible: false,
        });
      })
    )
  );
}
